import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Employee } from '../types';
import { strategicPerformanceSettingsAPI } from '../utils/apiService';
import { useOldStrategicPerformanceStore } from './useOldStrategicPerformanceStore';

export const useCurrentUser = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [, setEmployeesInState] = useOldStrategicPerformanceStore<Employee[]>('employees', []);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Employee[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUser = useMemo(
    () => availableUsers.find(employee => employee.id === currentUserId) ?? null,
    [availableUsers, currentUserId]
  );

  useEffect(() => {
    if (authLoading) {
      return;
    }

    const load = async () => {
      try {
        setLoading(true);

        if (!user) {
          setEmployees([]);
          setAvailableUsers([]);
          setCurrentUserId(null);
          setError('Unable to determine the logged in user.');
          return;
        }

        const employeeData = await strategicPerformanceSettingsAPI.employees.getAll();
        setEmployees(employeeData);
        setEmployeesInState(employeeData);

        const normalizedEmail = user.email?.toLowerCase();
        const isStrategicAdmin =
          !!user.isStrategicPerformanceSuperUser ||
          user.role === 'SuperUser' ||
          user.role === 'AppOwner';

        const matchedEmployee =
          employeeData.find(emp => {
            if (emp.userId) {
              if (user.id && emp.userId === user.id) {
                return true;
              }
              if ((user as any)._id && emp.userId === (user as any)._id) {
                return true;
              }
            }

            if (normalizedEmail && emp.email) {
              return emp.email.toLowerCase() === normalizedEmail;
            }

            return false;
          }) ?? null;

        if (isStrategicAdmin && !matchedEmployee) {
          const adminEmployee: Employee = {
            id: user.id,
            userId: user.id,
            fullName: user.displayName || user.email || 'Administrator',
            jobTitle: user.jobTitle || 'Administrator',
            email: user.email || '',
            departmentId: '',
            isDepartmentHead: true,
            isCEO: false,
            status: 'active',
          };

          const combinedEmployees = [...employeeData, adminEmployee];
          setEmployees(combinedEmployees);
          setEmployeesInState(combinedEmployees);
          setAvailableUsers(combinedEmployees);
          setCurrentUserId(adminEmployee.id);
          setError(null);
          setLoading(false);
          return;
        }

        const isStrategicPerformanceUser =
          isStrategicAdmin || !!matchedEmployee;

        if (!isStrategicPerformanceUser) {
          setAvailableUsers([]);
          setCurrentUserId(null);
          setError('You are not configured as a Strategic Performance user.');
          setEmployeesInState([]);
          return;
        }

        if (isStrategicAdmin) {
          setAvailableUsers(employeeData);
          const initialUser = matchedEmployee || employeeData[0] || null;
          setCurrentUserId(initialUser?.id ?? null);
        } else if (matchedEmployee) {
          setAvailableUsers([matchedEmployee]);
          setCurrentUserId(matchedEmployee.id);
        } else {
          setAvailableUsers([]);
          setCurrentUserId(null);
          setError('Your Strategic Performance profile could not be found.');
          setEmployeesInState([]);
          return;
        }

        setError(null);
      } catch (err: any) {
        console.error('Failed to load employees for current user hook:', err);
        setError(err.response?.data?.error || 'Failed to load employees');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, user]);

  const switchUser = (userId: string) => {
    const isStrategicAdmin =
      !!user?.isStrategicPerformanceSuperUser ||
      user?.role === 'SuperUser' ||
      user?.role === 'AppOwner';

    if (!isStrategicAdmin) {
      return;
    }

    const targetUser = employees.find(employee => employee.id === userId);

    if (!targetUser) {
      return;
    }

    setCurrentUserId(targetUser.id);
  };

  return {
    currentUserId,
    currentUser,
    switchUser,
    allUsers: availableUsers,
    loading: loading || authLoading,
    error,
    isAdmin:
      !!user?.isStrategicPerformanceSuperUser ||
      user?.role === 'SuperUser' ||
      user?.role === 'AppOwner',
  };
};
